import { Component, EventEmitter, Input, OnInit, Output, SimpleChanges } from '@angular/core';
import { Store } from '@ngrx/store';
import { BehaviorSubject, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { ApiService } from '../../services/api/api.service';
import { CommonUtilsService } from '../../services/commonutils/common-utils.service';
import { getCurrentOrganizationId$ } from '../../store/selectors/session.selector';
import { Masters } from '../../models/masters/masters.class';
import { Record } from '../../models/record/record.class';
import { RecordField } from '../../models/record/recordfield.class';
import { ViewRecordPagination } from '../../interface/viewrecordpagination.interface';

@Component({
    selector: 'lib-masterlookup',
    templateUrl: './masterlookup.component.html',
    styleUrls: ['./masterlookup.component.css'],
    standalone: false,
})
export class MasterlookupComponent implements OnInit {
    @Input() field: RecordField;
    @Input() value;
    @Input() classList;
    @Input() required;
    @Input() errors: any[];
    @Input() warnings: any[];
    @Input() masters: Masters;
    @Input() disabled: boolean;
    @Input() lookupHint: string;
    @Input() description: string;
    @Input() fieldsMap: BehaviorSubject<{
        [property: string]: {
            keyColumns: string[];
            valuesMap: { id: string; entityId: string; masterColumn: string; value: any; references: any[] }[];
        };
    }>;
    @Input() isMasterSearchDisabled: boolean;
    @Input() record: Record;

    @Output() modelChange = new EventEmitter();
    @Output() debounceEventHandler = new EventEmitter();
    @Output() selectedLookupRecord = new EventEmitter();

    options: { value: string; displayName: string }[] = [];
    allValues: any[] = [];
    unSubscribe = new Subject<void>();
    pagination: BehaviorSubject<ViewRecordPagination> = new BehaviorSubject(undefined);
    clearSearch = new Subject<void>();
    fieldsMapState: { keyColumns: string[]; valuesMap: { id: string; masterColumn: string; value: any; references: any[] }[] };
    currentOrganizationId: string;

    constructor(private _taxilla: ApiService, private store$: Store) {}

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.isMasterSearchDisabled) {
            if (!changes.isMasterSearchDisabled.currentValue) {
                this.resetAndStartFetchingValues(undefined, true);
            } else if (this.field.value !== undefined) {
                this.resetVariables();
                this.modelChanged(undefined);
            }
        }
    }

    resetAndStartFetchingValues = (fromEvent?: boolean, initialLoad?: boolean) => {
        const changed = this.checkChangeInReferences();
        fromEvent = fromEvent ? (changed ? fromEvent : false) : false;
        if (changed || fromEvent || initialLoad) {
            if (this.field.value === undefined && changed) {
                this.clearSearch.next();
                this.resetVariables();
            }
            (changed || (fromEvent && this.checkIfReferenceValueExists()) || initialLoad) && this.initiatePagination(fromEvent, undefined);
        }
    };

    checkChangeInReferences = () => {
        const filteredMap = this.field.filterMapWithFieldReference(this.fieldsMap.value);
        filteredMap.valuesMap.splice(filteredMap.keyColumns.indexOf(this.field.id));
        const changed = JSON.stringify(this.fieldsMapState) !== JSON.stringify(filteredMap);
        changed && (this.fieldsMapState = filteredMap);
        return changed;
    };

    checkIfReferenceValueExists = () => {
        const table = this.fieldsMapState[this.field.masterData.tableUid];
        const fieldIndexInMap = table?.valuesMap.findIndex(map => this.field.id === map.id);
        let exists = fieldIndexInMap > 0;
        table?.valuesMap
            ?.filter((_mapObject, index) => index < fieldIndexInMap)
            .forEach(mapObject => {
                exists = exists && mapObject.value?.length > 0;
            });
        return exists;
    };

    resetVariables = () => {
        this.options = [];
        this.allValues = [];
    };

    initiatePagination = (fromEvent: boolean, criteria: string) => {
        this.pagination.next({
            currentPageIndex: -1,
            lowerLimit: undefined,
            pageSize: undefined,
            upperLimit: undefined,
            viewIndex: undefined,
            filterTypeSearch: undefined,
            searchCriteria: (criteria || '') as any,
            state: fromEvent as any,
        });
    };

    startFetchingOptions = () => {
        const pagination = this.pagination.value;
        const fromEvent: boolean = pagination.state as any;
        const tablesMap = this.fieldsMap.value;
        const filteredMap = this.field.filterMapWithFieldReference(tablesMap);
        const fieldIndex = filteredMap.keyColumns.indexOf(this.field.id);
        if (fromEvent && fieldIndex === 0) {
            return;
        }
        if (pagination.state as any as boolean) {
            this.resetVariables();
        }
        const values = {};
        filteredMap.valuesMap
            .filter(valueMap => filteredMap.keyColumns.indexOf(valueMap.id) > -1 && valueMap.value !== undefined)
            .forEach(valueMap => {
                values[valueMap.masterColumn] = {
                    newValue: valueMap.value,
                };
            });
        if (fieldIndex === 0 || (fieldIndex > 0 && Object.keys(values).length === fieldIndex)) {
            this.fetchOptions(values, filteredMap.keyColumns.length);
        }
    };

    fetchOptions = (valuesMap, keyColumnsLength) => {
        const pagination = this.pagination.value;
        const map = this.checkMap(valuesMap);
        const methodName =
            map && Object.keys(map).length === keyColumnsLength && keyColumnsLength !== 0
                ? 'getMasterRecordWithprimaryValue'
                : 'getPrimaryValuesInMasterTable';
        const method: ApiService['masters']['getMasterRecordWithprimaryValue'] | ApiService['masters']['getPrimaryValuesInMasterTable'] =
            this._taxilla.masters[methodName];
        method(
            {
                masterDataRow: {
                    tableId: this.field.masterData.tableUid,
                    unitId: this.currentOrganizationId,
                    columnValues: map,
                },
                masterSearchDetail: {
                    pageNo: pagination.currentPageIndex + 1,
                    searchString: pagination.searchCriteria as any,
                },
            },
            {
                successCallback: response => {
                    const key = this.field.masterData.columnName;
                    (response || []).forEach(record => {
                        record.fields?.forEach(fld => {
                            if (fld.id === key && !this.allValues.includes(fld.value)) {
                                this.allValues.push(fld.value);
                                this.options.push({
                                    displayName: fld.value,
                                    value: fld.value,
                                });
                            }
                        });
                    });
                },
            }
        );
    };

    checkMap = map => {
        map &&
            Object.keys(map).forEach(mapId => {
                if (typeof map[mapId] === 'object') {
                    if (Object.keys(map[mapId]).length === 0) {
                        delete map[mapId];
                    } else {
                        map[mapId] = this.checkMap(map[mapId]);
                        if (Object.keys(map[mapId]).length === 0) {
                            delete map[mapId];
                        }
                    }
                } else if (typeof map[mapId] !== 'object' && map[mapId] === undefined) {
                    delete map[mapId];
                }
            });
        return map;
    };

    modelChanged = event => {
        if (
            (event && event.toLowerCase() === 'na') ||
            (event === undefined && this.field.value && (this.field.value as string).toLowerCase() === 'na')
        ) {
            return;
        }
        this.field.value = event;
        const map = CommonUtilsService.cloneObject(this.fieldsMap.value);
        const currentTableId = this.field.masterData.tableUid;
        const tableMap = map[currentTableId];
        const valueMapIndex = tableMap.keyColumns.indexOf(this.field.id);
        if (this.field.value !== undefined) {
            if (valueMapIndex === -1) {
                tableMap.keyColumns.push(this.field.id);
                tableMap.valuesMap.push({
                    id: this.field.id,
                    masterColumn: this.field.masterData.columnName,
                    value: this.field.value,
                });
            } else {
                tableMap.valuesMap[valueMapIndex] = {
                    id: this.field.id,
                    masterColumn: this.field.masterData.columnName,
                    value: this.field.value,
                };
                tableMap.valuesMap.filter((_valueMap, index) => index > valueMapIndex).forEach(valueMap => (valueMap.value = undefined));
            }
        } else {
            if (valueMapIndex === -1) {
                /** Do Nothing */
            } else {
                tableMap.valuesMap.filter((_valueMap, index) => index >= valueMapIndex).forEach(valueMap => (valueMap.value = undefined));
            }
        }
        this.modelChange && this.modelChange.emit(event);
    };

    searchChanged = (value: string) => {
        this.resetVariables();
        this.initiatePagination(false, value);
    };

    ngOnInit() {
        this.store$
            .select(getCurrentOrganizationId$)
            .pipe(takeUntil(this.unSubscribe))
            .subscribe(orgId => {
                this.currentOrganizationId = orgId;
            });
        this.fieldsMap
            .pipe(
                takeUntil(this.unSubscribe),
                filter(() => !this.isMasterSearchDisabled)
            )
            .subscribe(() => {
                this.resetAndStartFetchingValues(true);
            });
        this.pagination
            .pipe(
                takeUntil(this.unSubscribe),
                filter(data => data !== undefined)
            )
            .subscribe(() => {
                this.startFetchingOptions();
            });
    }

    ngOnDestroy(): void {
        this.unSubscribe.next();
        this.unSubscribe.complete();
        this.clearSearch.complete();
    }
}
